import classNames from 'classnames';
import {useRouter} from 'next/router';
import {type FC, type MouseEvent, useCallback, useMemo} from 'react';
import {MYPAGE, MYPAGE_INTERESTS} from '../../../config/constants/routes';
import {isReleasedRedesignTop} from '../../../config/unReleasePage';
import {QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE} from '../../../core/entities/login/constants';

type Props = {
  showMyPage?: boolean;
  scoutsCount?: number;
  interestsCount?: number;
  onItemClick?: () => void;
  onLogout?: () => void;
  className?: string;
  inactive?: boolean;
};

type MenuItem = {
  id?: string;
  href?: string;
  label: string;
  showWhenInactive?: boolean;
};

const getMenuItems = (): MenuItem[] => {
  if (isReleasedRedesignTop()) {
    return [
      {id: 'login', href: '/login', label: 'ログイン'},
      {
        id: 'mypage',
        href: MYPAGE,
        label: 'マイページ',
      },
      {href: '/mypage/profile', label: 'プロフィールの編集'},
      {
        href: '/mypage/profile',
        label: '休会を解除',
        showWhenInactive: true,
      },
    ];
  }

  return [
    {id: 'login', href: '/login', label: 'ログイン'},
    {
      id: 'mypage',
      href: '/',
      label: 'マイページ',
    },
    {id: 'scouts', href: '/scouts', label: 'スカウト'},
    {id: 'interests', href: MYPAGE_INTERESTS, label: '興味あり'},
    {href: '/schedule', label: 'スケジュール'},
    {href: '/mypage/profile', label: 'プロフィールの編集'},
    {href: '/mypage/reports', label: '選考体験記の管理'},
    {href: '/mypage/reports/add', label: '選考体験記の投稿'},
    {href: '/mypage/entrysheets', label: 'エントリシートの管理'},
    {href: '/mypage/es/new', label: 'エントリシートの投稿'},
    {href: '/mypage/profile', label: '休会を解除', showWhenInactive: true},
    {id: 'logout', label: 'ログアウト'},
  ] satisfies MenuItem[];
};

const filterMenuItems = (
  items: MenuItem[],
  showMyPage: boolean | undefined,
  inactive: boolean,
): MenuItem[] => {
  return items.filter((item) => {
    if (!inactive && item.showWhenInactive) return false;

    const {id} = item;

    if (!showMyPage && id !== 'login') return false;
    if (showMyPage && id === 'login') return false;
    if (id === 'mypage' && !showMyPage) return false;

    return true;
  });
};

const getNotificationCount = (
  itemId: string | undefined,
  scoutsCount: number | undefined,
  interestsCount: number | undefined,
): number => {
  switch (itemId) {
    case 'scouts':
      return scoutsCount ?? 0;
    case 'interests':
      return interestsCount ?? 0;
    default:
      return 0;
  }
};

type MenuItemProps = {
  item: MenuItem;
  notificationCount: number;
  router: ReturnType<typeof useRouter>;
  onClickFactory: (item: MenuItem) => (e: MouseEvent) => void;
};

const MenuItemComponent: FC<MenuItemProps> = ({
  item,
  notificationCount,
  router,
  onClickFactory,
}) => {
  const {id, href, label} = item;

  if (id === 'logout') {
    return (
      <button
        onClick={onClickFactory(item)}
        className="flex justify-between p-8 cursor-pointer w-full"
      >
        <span>{label}</span>
      </button>
    );
  }

  const linkHref =
    id === 'login'
      ? `${href}?${QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE}=${router.asPath}`
      : href;

  return (
    <a
      href={linkHref}
      onClick={onClickFactory(item)}
      className="flex justify-between p-8 cursor-pointer"
    >
      <span>{label}</span>
      {notificationCount > 0 && (
        <span className="min-w-[28px] rounded-full bg-notification-2 p-4 text-center text-sm font-bold text-white">
          {notificationCount >= 100 ? '99+' : notificationCount}
        </span>
      )}
    </a>
  );
};

export const HeaderMenu: FC<Props> = ({
  showMyPage,
  scoutsCount,
  interestsCount,
  onItemClick,
  onLogout,
  className,
  inactive = false,
}) => {
  const router = useRouter();

  const menuItems = useMemo(() => getMenuItems(), []);

  const filteredItems = useMemo(
    () => filterMenuItems(menuItems, showMyPage, inactive),
    [menuItems, showMyPage, inactive],
  );

  const onClickFactory = useCallback(
    (item: MenuItem) => (e: MouseEvent) => {
      e.stopPropagation();
      const {id} = item;
      if (id === 'logout') {
        onLogout?.();
      }
      onItemClick?.();
    },
    [onItemClick, onLogout],
  );

  return (
    <ul
      className={classNames(
        'bg-white divide-line pc:text-base text-md flex flex-col divide-y py-4 px-12 w-[184px]',
        className,
      )}
    >
      {filteredItems.map((item) => {
        const notificationCount = getNotificationCount(
          item.id,
          scoutsCount,
          interestsCount,
        );

        return (
          <li key={item.id || item.href}>
            <MenuItemComponent
              item={item}
              notificationCount={notificationCount}
              router={router}
              onClickFactory={onClickFactory}
            />
          </li>
        );
      })}
    </ul>
  );
};
